import React from 'react';
import StringGroupDetailPage from 'src/features/string-group-detail/StringGroupDetailPage';
import { Routes, Route, Navigate } from 'react-router';
import LandingEntry from 'src/features/landing/LandingEntry';
import { NamespaceDetailPage } from 'src/features/namespace';
import SearchPage from 'src/features/search/SearchPage';
import { TrackTranslationsPage } from 'src/features/track';
import StringDetailPage from 'src/features/string/StringDetailPage';
import useFederatedAuth, {
  AuthenticationState,
} from 'src/components/auth/FederatedAuth';
import { AuthContext } from 'src/components/auth/AuthContext';
import AppBootstrap from 'src/components/auth/AppBootstrap';
import AppShell from 'src/common/ui/AppShell';
import { FailureStatus } from 'src/common/ui/StatusScreen';
import LandingPage from 'src/features/landing/LandingPage';

// Injected by webpack DefinePlugin (see configuration/configUtils.js). True for
// local/non-production builds so the app renders without a Midway/Cognito
// round-trip; false in production where authentication is required.
declare const NO_AUTH: boolean;

interface AppCoreProps {
  authState: AuthenticationState;
  userId: string | null;
}

// The authenticated application. Routing lives here so the landing page (and
// future pages) only mount once the user is signed in and a JWT has been stored
// for the AppSync client to attach to its requests.
//
// AppBootstrap wraps the whole route table rather than sitting inside it as a
// layout route: every route is then behind the gate by construction, and no
// page-level query can be issued before the bootstrap profile query resolves.
// On failure the gate renders its error page here, in place, leaving the URL the
// user is on untouched.
const AuthenticatedApp: React.FC = () => (
  <AppBootstrap>
    <Routes>
      <Route element={<AppShell />}>
        {/* Static /search outranks dynamic /:namespaceId by react-router
            specificity, so a namespace literally named "search" cannot be
            reached via this route. */}
        <Route path="/search" element={<SearchPage />} />
        <Route
          path="/:namespaceId/:stringGroupId"
          element={<StringGroupDetailPage />}
        />
        <Route
          path="/trackMyTranslations"
          element={<TrackTranslationsPage />}
        />
        {/* /:namespaceId is a 1-segment catch-all for namespace detail pages.
            React Router v6 ranks static routes (/search, /trackMyTranslations)
            higher than parameterized segments, so they are not shadowed. */}
        <Route path="/:namespaceId" element={<NamespaceDetailPage />} />
        {/* String Detail page: /:namespaceId/:stringGroupId/:stringId (+ optional ?variant=)
            NOTE: This is a broad catch-all for ANY 3-segment path (unprefixed per user request).
            Future routes with exactly 3 segments could collide — evaluate ordering carefully. */}
        <Route
          path="/:namespaceId/:stringGroupId/:stringId"
          element={<StringDetailPage />}
        />
      </Route>
      <Route path="/" element={<LandingEntry />} />
      <Route path="/home" element={<LandingPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  </AppBootstrap>
);

const AppCore: React.FC<AppCoreProps> = ({ authState, userId }) => {
  const body = (() => {
    switch (authState) {
      case AuthenticationState.LOGGING_IN:
        return (
          <div id="langhub-status" role="status">
            Logging in&hellip;
          </div>
        );
      case AuthenticationState.LOGGED_IN:
        // The bootstrap gate wraps routing, so the profile query resolves
        // before any route — and so any page-level query — mounts.
        return <AuthenticatedApp />;
      default:
        return (
          <FailureStatus heading="Failed to sign in">
            <p>
              Make sure you are on the Amazon network and authenticated, then
              refresh this page.
            </p>
          </FailureStatus>
        );
    }
  })();

  return (
    <AuthContext.Provider value={{ authState, userId }}>
      {body}
    </AuthContext.Provider>
  );
};

/** With Midway authentication (production). */
const AppWithAuth: React.FC = () => {
  const { authState, userId } = useFederatedAuth();
  return <AppCore authState={authState} userId={userId} />;
};

/** Without authentication (local/dev/test builds). */
const AppNoAuth: React.FC = () => (
  <AppCore authState={AuthenticationState.LOGGED_IN} userId="no-auth-user" />
);

const App: React.FC = NO_AUTH ? AppNoAuth : AppWithAuth;

export default App;
